/**
 * The line that says whether the work is being kept.
 *
 * Autosave nobody can see is indistinguishable from autosave that stopped
 * working — and the point of the feature is that a creator can stop worrying about
 * work they are in the middle of. So the studio says it, quietly, in one line that
 * changes only when it has something to say.
 *
 * The wording is deliberately *device*-local. The copy is not a draft: it does not
 * follow the creator to another phone, and pretending otherwise would be the most
 * expensive kind of lie — the one that is believed until the day the work is
 * needed. Saving a draft stays the thing that makes work real.
 *
 * Shared by both studios, because it is the same sentence about the same
 * copy; only the id differs, and that is a prop so each suite keeps a handle on
 * its own.
 */
import {StyleSheet, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {type AutosaveState} from '#/components/studio/useStudioAutosave'
import {Text} from '#/components/Typography'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

export function StudioAutosaveNote({
  state,
  testID,
}: {
  state: AutosaveState
  testID: string
}) {
  const styles = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()

  // Nothing to report: no unsaved work.
  if (state === 'idle') return null

  const failed = state === 'failed'
  const ink = failed ? c.warning : c.textFaint

  return (
    <View style={[styles.note, failed && styles.noteFailed]} testID={testID}>
      <Feather
        name={failed ? 'alert-circle' : state === 'saving' ? 'clock' : 'check'}
        size={11}
        color={ink}
      />
      <Text
        style={[styles.noteTxt, {color: ink}]}
        numberOfLines={1}
        accessibilityLiveRegion="polite">
        {failed
          ? l`Could not keep a copy on this device — save a draft to be sure`
          : state === 'saving'
            ? l`Keeping a copy…`
            : l`Copy kept on this device`}
      </Text>
    </View>
  )
}

/**
 * A hairline row rather than a card.
 *
 * It sits between the header and the work, so it has to read as part of the
 * studio's chrome and not as another thing to look at. The failure state is the
 * one exception: it earns a tint, because a creator who has been told their work
 * is safe needs to notice immediately when it is not.
 */
const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    note: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 14,
      paddingVertical: 5,
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: c.border,
    },
    noteFailed: {
      backgroundColor: c.warningTint,
      borderColor: c.warningBorder,
    },
    noteTxt: {flex: 1, fontSize: 11, fontWeight: '600'},
  })

const useStyles = () => useStudioStyles(makeStyles)
